import { useCallback, useEffect, useRef, useState } from "react";
import { Repeat, RotateCcw } from "lucide-react";

/** Compact m:ss formatter — fits neatly inside a marker pill. */
const fmtMarker = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * A↔B loop range editor — only mounted when transport loop is ON.
 *
 * Drag handling: window-level pointer listeners are bound SYNCHRONOUSLY inside
 * `pointerdown`, NOT via a useEffect.  This is critical for two reasons:
 *   1. React useEffect runs after commit; if the user moves the pointer between
 *      `pointerdown` and the next paint, the move events fire before the listener
 *      is attached and are lost.
 *   2. Playwright (and human fast-flicks) routinely fire pointermove ≤16 ms after
 *      pointerdown — well within the render-commit window. Synchronous listener
 *      attachment is the only correct fix.
 *
 * Refs (loopStart/loopEnd/maxDur/onChange/pixelsToTime) carry the latest values
 * into the event handlers so we never read stale closure data mid-drag.
 */
export default function LoopMarkers({ enabled, loopStart, loopEnd, maxDur, onChange, onReset }) {
  const trackRef = useRef(null);
  const draggingRef = useRef(null);          // null | "A" | "B"
  const [dragging, setDragging] = useState(null); // mirror for cursor styling only

  // Refs that the drag handlers read — always current.
  const loopStartRef = useRef(loopStart);
  const loopEndRef = useRef(loopEnd);
  const maxDurRef = useRef(maxDur);
  const onChangeRef = useRef(onChange);
  useEffect(() => { loopStartRef.current = loopStart; }, [loopStart]);
  useEffect(() => { loopEndRef.current = loopEnd; }, [loopEnd]);
  useEffect(() => { maxDurRef.current = maxDur; }, [maxDur]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const safeMax = maxDur > 0 ? maxDur : 1;
  const pct = (t) => Math.max(0, Math.min(100, (t / safeMax) * 100));
  const startPct = pct(loopStart);
  const endPct = pct(loopEnd);

  // Convert a screen-X coordinate to a time in seconds within the track.
  const pixelsToTime = useCallback((clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || maxDurRef.current <= 0) return 0;
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    return (x / rect.width) * maxDurRef.current;
  }, []);

  const startDrag = (which) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (maxDurRef.current <= 0) return;

    draggingRef.current = which;
    setDragging(which);

    const MIN_RANGE = 0.1; // 100 ms minimum loop window

    // iter-265 — When dragging the region BODY (not an A/B endpoint),
    // anchor both edges to the original positions and move them by the
    // same delta so the loop length is preserved exactly.
    const anchorStart = loopStartRef.current;
    const anchorEnd = loopEndRef.current;
    const anchorPointerTime = which === "region" ? pixelsToTime(e.clientX) : 0;

    const onMove = (mv) => {
      const t = pixelsToTime(mv.clientX);
      const s = loopStartRef.current;
      const en = loopEndRef.current;
      const md = maxDurRef.current;
      if (draggingRef.current === "A") {
        const newStart = Math.max(0, Math.min(en - MIN_RANGE, t));
        onChangeRef.current?.(newStart, en);
      } else if (draggingRef.current === "B") {
        const newEnd = Math.max(s + MIN_RANGE, Math.min(md, t));
        onChangeRef.current?.(s, newEnd);
      } else if (draggingRef.current === "region") {
        // Shift both endpoints by the pointer delta, clamped so the
        // region never escapes [0, maxDur] AND keeps the same length.
        const len = anchorEnd - anchorStart;
        let delta = t - anchorPointerTime;
        const minDelta = -anchorStart;            // can't drag start below 0
        const maxDelta = md - anchorEnd;          // can't drag end past maxDur
        delta = Math.max(minDelta, Math.min(maxDelta, delta));
        const newStart = anchorStart + delta;
        const newEnd = newStart + len;
        onChangeRef.current?.(newStart, newEnd);
      }
    };
    const onUp = () => {
      draggingRef.current = null;
      setDragging(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const onTrackClick = (e) => {
    if (maxDurRef.current <= 0 || draggingRef.current) return;
    const t = pixelsToTime(e.clientX);
    const s = loopStartRef.current;
    const en = loopEndRef.current;
    if (Math.abs(t - s) < Math.abs(t - en)) {
      onChangeRef.current?.(Math.max(0, Math.min(en - 0.1, t)), en);
    } else {
      onChangeRef.current?.(s, Math.max(s + 0.1, Math.min(maxDurRef.current, t)));
    }
  };

  if (!enabled) return null;

  const noAudio = maxDur <= 0;
  return (
    <div
      data-testid="loop-markers-row"
      className="mb-3 flex items-center gap-3 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/[0.04] px-3 py-2"
    >
      <div className="flex w-24 items-center gap-1.5">
        <Repeat className="h-3 w-3 text-fuchsia-300" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-fuchsia-200">LOOP&nbsp;A↔B</span>
      </div>
      <div
        ref={trackRef}
        onClick={onTrackClick}
        data-testid="loop-markers-track"
        className={`relative h-7 flex-1 select-none rounded-md bg-black/40 ring-1 ring-white/5 ${noAudio ? "opacity-50" : "cursor-pointer"}`}
      >
        {/* Range highlight — iter-265: now grabbable. Drag the body
            to move the WHOLE loop region while preserving its length. */}
        {!noAudio && (
          <div
            data-testid="loop-range-fill"
            onPointerDown={startDrag("region")}
            onClick={(e) => e.stopPropagation()}
            title="Drag to move the loop region (length preserved)"
            aria-label="Drag loop region"
            className={`absolute inset-y-0 rounded-sm bg-fuchsia-500/25 transition-colors hover:bg-fuchsia-500/35 ${dragging === "region" ? "bg-fuchsia-500/45" : ""} touch-none cursor-grab active:cursor-grabbing`}
            style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
          />
        )}
        {/* A & B vertical guides */}
        {!noAudio && (
          <>
            <div className="pointer-events-none absolute inset-y-0 w-[2px] bg-fuchsia-400" style={{ left: `${startPct}%` }} />
            <div className="pointer-events-none absolute inset-y-0 w-[2px] bg-amber-300" style={{ left: `${endPct}%` }} />
          </>
        )}
        {/* A knob */}
        <button
          data-testid="loop-marker-a"
          onPointerDown={startDrag("A")}
          disabled={noAudio}
          aria-label={`Loop start at ${fmtMarker(loopStart)}`}
          aria-grabbed={dragging === "A"}
          className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 cursor-grab touch-none active:cursor-grabbing disabled:cursor-not-allowed"
          style={{ left: `${startPct}%` }}
        >
          <span className="block rounded-full bg-fuchsia-400 px-1.5 py-0.5 font-mono text-[9px] font-bold text-black shadow-[0_2px_6px_rgba(217,70,239,0.6)] ring-1 ring-fuchsia-200/50">
            A&nbsp;{fmtMarker(loopStart)}
          </span>
        </button>
        {/* B knob */}
        <button
          data-testid="loop-marker-b"
          onPointerDown={startDrag("B")}
          disabled={noAudio}
          aria-label={`Loop end at ${fmtMarker(loopEnd)}`}
          aria-grabbed={dragging === "B"}
          className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 cursor-grab touch-none active:cursor-grabbing disabled:cursor-not-allowed"
          style={{ left: `${endPct}%` }}
        >
          <span className="block rounded-full bg-amber-300 px-1.5 py-0.5 font-mono text-[9px] font-bold text-black shadow-[0_2px_6px_rgba(252,211,77,0.5)] ring-1 ring-amber-100/50">
            B&nbsp;{fmtMarker(loopEnd)}
          </span>
        </button>
      </div>
      <button
        data-testid="loop-reset-btn"
        onClick={onReset}
        disabled={noAudio}
        title="Reset A↔B to full mix"
        className="flex-shrink-0 rounded-md border border-white/10 bg-white/[0.06] px-2 py-1 text-white/70 transition hover:bg-white/[0.12] disabled:opacity-40"
      >
        <RotateCcw className="h-3 w-3" />
      </button>
    </div>
  );
}
